import { useState, useEffect } from "react";
import { authFetch } from "../api";
import Header from "../components/Header";
import NeighborhoodSelect from "../components/NeighborhoodSelect";
import ListingEditModal from "../components/ListingEditModal";

const FIELDS = [
  { key: "sqft", label: "Sqft", format: (v, est) => v ? `${v.toLocaleString()}${est ? " (est)" : ""}` : "-" },
  { key: "bedrooms", label: "Beds", format: (v) => v ?? "-" },
  { key: "bathrooms", label: "Baths", format: (v) => v ?? "-" },
  { key: "dishwasher", label: "DW", format: (v) => v === true ? "Y" : v === false ? "N" : v ?? "-" },
  { key: "washer_dryer", label: "W/D", format: (v) => v || "-" },
  { key: "outdoor_space", label: "Outdoor", format: (v) => v === true ? "Y" : v === false ? "N" : "-" },
  { key: "renovation", label: "Reno", format: (v) => v || "-" },
  { key: "premium_views", label: "Views", format: (v) => v || "-" },
  { key: "floor", label: "Floor", format: (v) => v || "-" },
  { key: "floor_line", label: "Line", format: (v) => v || "-" },
];

const PATCH_FIELD_MAP = {
  sqft: "sqft",
  bedrooms: "bedrooms",
  bathrooms: "bathrooms",
  dishwasher: "dishwasher",
  washer_dryer: "washer_dryer",
  outdoor_space: "private_outdoor_space",
  renovation: "renovation_level",
  premium_views: "premium_views",
  floor: "floor",
  floor_line: "floor_line",
};

const formatDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
};

export default function UnitMergePage() {
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
  const [search, setSearch] = useState("");
  const [units, setUnits] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState({});
  const [editModalListing, setEditModalListing] = useState(null);

  useEffect(() => {
    authFetch("/sale-reviews/neighborhoods").then((data) => {
      setNeighborhoods(data.neighborhoods || []);
    }).catch((err) => console.error("Error loading neighborhoods:", err));
  }, []);

  useEffect(() => {
    if (!selectedNeighborhood) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNeighborhood, page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        neighborhood_id: selectedNeighborhood,
        page,
        limit: 50,
        mismatch_only: true,
      });
      if (search) params.append("search", search);
      const data = await authFetch(`/listings/unit-merge?${params}`);
      setUnits(data.units || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Failed to load unit merge data:", err);
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadData();
  };

  const openEditModal = async (listingId) => {
    try {
      const res = await authFetch(`/listings/data-quality?listing_id=${listingId}`);
      const listing = res.listings?.[0];
      if (listing) setEditModalListing(listing);
    } catch (err) {
      console.error("Failed to load listing:", err);
    }
  };

  const mergeField = async (sourceListingId, targetListingId, fieldKey, sourceValue) => {
    const patchField = PATCH_FIELD_MAP[fieldKey];
    if (!patchField) return;
    const mergeKey = `${targetListingId}-${fieldKey}`;
    setMerging((prev) => ({ ...prev, [mergeKey]: true }));
    try {
      await authFetch(`/listings/${targetListingId}`, {
        method: "PATCH",
        body: JSON.stringify({ [patchField]: sourceValue }),
      });
      await loadData();
    } catch (err) {
      console.error("Merge failed:", err);
    }
    setMerging((prev) => ({ ...prev, [mergeKey]: false }));
  };

  const containerStyle = {
    maxWidth: 1400,
    margin: "0 auto",
    padding: "20px",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };

  const badgeStyle = (color) => ({
    display: "inline-block",
    padding: "1px 6px",
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 600,
    color: "#fff",
    backgroundColor: color,
    marginLeft: 4,
  });

  const cellStyle = (isMismatch) => ({
    padding: "4px 6px",
    fontSize: 12,
    borderBottom: "1px solid #e0e0e0",
    background: isMismatch ? "#fff3e0" : "transparent",
    whiteSpace: "nowrap",
    textAlign: "center",
  });

  const thStyle = {
    padding: "4px 6px",
    textAlign: "center",
    borderBottom: "2px solid #ddd",
    fontSize: 11,
    fontWeight: 600,
    background: "#f8f9fa",
  };

  return (
    <div>
      <Header showLogout />
      <div style={containerStyle}>
        <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 16 }}>Unit Data Merge</h1>
        <p style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
          Compare sale and rental data for the same unit. Click arrows to copy values, or pencil to edit.
        </p>

        {/* Filters */}
        <form onSubmit={handleSearch} style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "flex-end" }}>
          <NeighborhoodSelect
            neighborhoods={neighborhoods}
            selected={selectedNeighborhood}
            onChange={(id) => { setSelectedNeighborhood(id); setPage(1); }}
            multi={false}
            label="Neighborhood"
            style={{ minWidth: 200 }}
          />
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Address..."
              style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, width: 200 }}
            />
          </div>
          <button
            type="submit"
            style={{ padding: "6px 16px", background: "#333", color: "#fff", border: "none", borderRadius: 4, fontSize: 13, cursor: "pointer" }}
          >
            Search
          </button>
          <span style={{ fontSize: 13, color: "#666", marginLeft: 8 }}>
            {total > 0 && `${units.length} mismatches shown (${total} total matched units)`}
          </span>
        </form>

        {loading && <div style={{ padding: 20, color: "#666" }}>Loading...</div>}

        {!loading && units.length > 0 && units.map((unit) => {
          const unitKey = `${unit.property_id}-${unit.unit_number}`;
          const allListings = [...unit.sales, ...unit.rentals];

          const mismatchKey = (fieldKey) => {
            const map = { outdoor_space: "outdoor", washer_dryer: "wd", dishwasher: "dw", renovation: "reno", premium_views: "views" };
            return unit.mismatches.includes(map[fieldKey] || fieldKey);
          };

          return (
            <div key={unitKey} style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 6, marginBottom: 12, padding: 12 }}>
              {/* Unit header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{unit.address} #{unit.unit_number}</span>
                  <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>
                    ({unit.sales.length}S / {unit.rentals.length}R)
                  </span>
                  {unit.mismatches.map((m) => (
                    <span key={m} style={badgeStyle("#e65100")}>{m}</span>
                  ))}
                </div>
              </div>

              {/* Comparison table */}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: "left", width: 70 }}>Type</th>
                    <th style={{ ...thStyle, textAlign: "left", width: 90 }}>Status</th>
                    {FIELDS.map((f) => (
                      <th key={f.key} style={thStyle}>{f.label}</th>
                    ))}
                    <th style={{ ...thStyle, width: 110 }}>Updated</th>
                    <th style={{ ...thStyle, width: 30 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {allListings.map((listing, idx) => {
                    const isSale = listing.type === "S";
                    const typeColor = isSale ? "#1565c0" : "#2e7d32";
                    const typeLabel = isSale ? "SALE" : "RENTAL";

                    // For merge arrows: find a target listing of the opposite type
                    const oppositeListings = isSale ? unit.rentals : unit.sales;

                    return (
                      <tr key={listing.id} style={{ borderBottom: idx === unit.sales.length - 1 ? "2px solid #ccc" : undefined }}>
                        {/* Type + badges */}
                        <td style={{ padding: "4px 6px", borderBottom: "1px solid #e0e0e0", fontSize: 11, fontWeight: 600, color: typeColor }}>
                          {listing.listing_url ? (
                            <a href={listing.listing_url} target="_blank" rel="noreferrer" style={{ color: typeColor, textDecoration: "none" }}>
                              {typeLabel}
                            </a>
                          ) : typeLabel}
                          {listing.verified && <span style={badgeStyle("#4caf50")}>V</span>}
                          {listing.se_enriched && <span style={badgeStyle("#2196f3")}>SE</span>}
                        </td>

                        {/* Status */}
                        <td style={{ padding: "4px 6px", borderBottom: "1px solid #e0e0e0", fontSize: 10 }}>
                          <span style={{
                            padding: "1px 5px",
                            borderRadius: 3,
                            background: listing.listing_status === "ACTIVE" ? "#fff9c4"
                              : listing.listing_status === "RENTED" || listing.listing_status === "CLOSED" ? "#c8e6c9"
                              : "#f5f5f5",
                            fontSize: 10,
                          }}>
                            {listing.listing_status}
                          </span>
                        </td>

                        {/* Data fields */}
                        {FIELDS.map((f) => {
                          const isMismatch = mismatchKey(f.key);
                          const val = listing[f.key];
                          const est = f.key === "sqft" ? listing.sqft_estimated : false;
                          const mKey = `${listing.id}-${f.key}`;

                          return (
                            <td key={f.key} style={cellStyle(isMismatch)}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>
                                {/* Up arrow for rental rows (copy to sale) */}
                                {!isSale && isMismatch && val != null && oppositeListings.length > 0 && (
                                  <button
                                    style={{ border: "none", background: "none", cursor: "pointer", fontSize: 13, padding: 0, color: "#1976d2" }}
                                    title={`Copy to sale #${oppositeListings[0].id}`}
                                    onClick={() => mergeField(listing.id, oppositeListings[0].id, f.key, val)}
                                    disabled={merging[mKey]}
                                  >
                                    {merging[mKey] ? "..." : "\u2191"}
                                  </button>
                                )}
                                <span>{f.format(val, est)}</span>
                                {/* Down arrow for sale rows (copy to rental) */}
                                {isSale && isMismatch && val != null && oppositeListings.length > 0 && (
                                  <button
                                    style={{ border: "none", background: "none", cursor: "pointer", fontSize: 13, padding: 0, color: "#1976d2" }}
                                    title={`Copy to rental #${oppositeListings[0].id}`}
                                    onClick={() => mergeField(listing.id, oppositeListings[0].id, f.key, val)}
                                    disabled={merging[mKey]}
                                  >
                                    {merging[mKey] ? "..." : "\u2193"}
                                  </button>
                                )}
                              </div>
                            </td>
                          );
                        })}

                        {/* Updated / Verified date */}
                        <td style={{ padding: "4px 6px", borderBottom: "1px solid #e0e0e0", fontSize: 10, color: "#666", whiteSpace: "nowrap" }}>
                          {listing.verified_at ? (
                            <span style={{ color: "#2e7d32" }} title={`Verified: ${listing.verified_at}`}>
                              V: {formatDate(listing.verified_at)}
                            </span>
                          ) : listing.se_updated_at ? (
                            <span title={`SE enriched: ${listing.se_updated_at}`}>
                              SE: {formatDate(listing.se_updated_at)}
                            </span>
                          ) : listing.updated_at ? (
                            <span title={`Updated: ${listing.updated_at}`}>
                              {formatDate(listing.updated_at)}
                            </span>
                          ) : "-"}
                        </td>

                        {/* Edit */}
                        <td style={{ padding: "4px 6px", borderBottom: "1px solid #e0e0e0", textAlign: "center" }}>
                          <span
                            title="Edit listing"
                            onClick={() => openEditModal(listing.id)}
                            style={{ fontSize: 14, cursor: "pointer", opacity: 0.5 }}
                          >&#9998;</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}

        {!loading && units.length === 0 && selectedNeighborhood && (
          <div style={{ padding: 40, textAlign: "center", color: "#999" }}>
            No mismatches found
          </div>
        )}

        {/* Pagination */}
        {total > 50 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 16 }}>
            <button
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              style={{ padding: "6px 16px", border: "1px solid #ccc", borderRadius: 4, cursor: page <= 1 ? "default" : "pointer" }}
            >
              Previous
            </button>
            <span style={{ padding: "6px 0", fontSize: 13 }}>Page {page}</span>
            <button
              disabled={units.length < 50}
              onClick={() => setPage(page + 1)}
              style={{ padding: "6px 16px", border: "1px solid #ccc", borderRadius: 4, cursor: units.length < 50 ? "default" : "pointer" }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModalListing && (
        <ListingEditModal
          listing={editModalListing}
          onClose={() => setEditModalListing(null)}
          onSave={async () => { await loadData(); }}   /* Save keeps the editor open */
          onVerify={async () => { setEditModalListing(null); await loadData(); }}
        />
      )}
    </div>
  );
}
